import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, Crown, Shield, Layers } from "lucide-react";
import { toast } from "sonner";
import ForgotPasswordModal from "../components/ForgotPasswordModal";

const ROLES = [
  {
    id: "platform",
    label: "Platform",
    sub: "Manage cafes",
    icon: Layers,
    placeholder: "email or phone",
  },
  {
    id: "owner",
    label: "Owner",
    sub: "Analytics",
    icon: Crown,
    placeholder: "email or phone",
  },
  {
    id: "admin",
    label: "Club",
    sub: "Run tables",
    icon: Shield,
    placeholder: "email or phone",
  },
];

export default function Login() {
  const { login, consumeExpiredFlag } = useAuth();
  const [role, setRole] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Default ON — most cafes sign in once on a shared device and never want
  // to be kicked out. Persisted to localStorage so the choice sticks.
  const [remember, setRemember] = useState(() => {
    try {
      const v = window.localStorage.getItem("cl_remember");
      return v === null ? true : v === "1";
    } catch {
      return true;
    }
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  // If we landed here because the previous session expired, show a single
  // friendly toast instead of a scary error.
  useEffect(() => {
    if (consumeExpiredFlag && consumeExpiredFlag()) {
      toast.info("Welcome back — please sign in to continue.");
    }
  }, [consumeExpiredFlag]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      window.localStorage.setItem("cl_remember", remember ? "1" : "0");
    } catch { /* ignore quota / private-mode errors */ }
    const res = await login(email.trim(), password, remember);
    setBusy(false);
    if (!res.ok) {
      // Any auth failure on the login screen is surfaced as a wrong-password
      // message — the backend already returns 401 for both bad-email and
      // bad-password, so this matches product spec.
      setErr("You have entered a wrong password");
    }
  };

  const placeholder = ROLES.find((r) => r.id === role)?.placeholder;

  return (
    <div className="min-h-screen w-full bg-[#0a0f0d] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]" aria-hidden>
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#10b981] blur-3xl" />
        <div className="absolute -bottom-40 -right-32 w-[520px] h-[520px] rounded-full bg-[#d4af37] blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="relative w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#d4af37] grid place-items-center text-black font-black shadow-lg shadow-amber-500/20">
              8
            </div>
            <span className="text-xs tracking-[0.35em] uppercase text-amber-400/80">
              Cue Genic
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white">
            Staff Console
          </h1>
          <p className="text-gray-400 mt-2 text-sm tracking-wide">
            Choose your role &amp; sign in
          </p>
        </div>

        <div
          className="bg-[#121a16] border border-white/10 rounded-2xl p-8 shadow-[0_30px_60px_rgba(0,0,0,0.5)] backdrop-blur-xl"
          data-testid="login-form"
        >
          <div className="grid grid-cols-3 gap-2 mb-6">
            {ROLES.map(({ id, label, sub, icon: Icon }) => (
              <button
                key={id}
                type="button"
                data-testid={`role-${id}`}
                onClick={() => setRole(id)}
                className={`relative p-2.5 rounded-lg border-2 text-left transition ${
                  role === id
                    ? "border-amber-400 bg-amber-400/10"
                    : "border-white/10 hover:border-white/20"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon
                    className={`w-3.5 h-3.5 ${
                      role === id ? "text-amber-300" : "text-gray-500"
                    }`}
                  />
                  <span
                    className={`font-black uppercase tracking-widest text-xs ${
                      role === id ? "text-amber-200" : "text-gray-300"
                    }`}
                  >
                    {label}
                  </span>
                </div>
                <div className="text-[9px] uppercase tracking-wider text-gray-500">
                  {sub}
                </div>
              </button>
            ))}
          </div>

          {/* Unified sign-in: email OR phone in the same field */}
          <form className="space-y-5" onSubmit={onSubmit} data-testid="login-email-form">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                Email or Phone
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  data-testid="login-email-input"
                  type="text"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={placeholder}
                  className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-amber-500/60"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
                  Password
                </Label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  data-testid="forgot-password-link"
                  className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 hover:text-amber-200"
                >
                  Forgot?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-amber-500/60"
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
              >
                {err}
              </div>
            )}

            <label
              htmlFor="login-remember"
              className="flex items-center gap-3 cursor-pointer select-none group pt-1"
              data-testid="login-remember-label"
            >
              <span className="relative inline-flex">
                <input
                  id="login-remember"
                  data-testid="login-remember-checkbox"
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="peer w-5 h-5 appearance-none rounded-md border-2 border-white/15 bg-black/40 checked:bg-[#d4af37] checked:border-[#d4af37] transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-amber-400/60 focus-visible:outline-none"
                />
                <svg
                  className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-black opacity-0 peer-checked:opacity-100 transition-opacity"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="flex flex-col leading-tight">
                <span className="text-sm text-gray-200 font-medium tracking-wide group-hover:text-white transition-colors">
                  Remember this device for 1 year
                </span>
                <span className="text-[11px] text-gray-500 tracking-wide">
                  {remember
                    ? "You won't need to sign in again on this device"
                    : "You'll stay signed in for ~30 days while active"}
                </span>
              </span>
            </label>

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-12 bg-[#d4af37] hover:bg-[#e2c258] text-black font-bold tracking-widest uppercase rounded-lg hover:-translate-y-0.5 transition-transform shadow-lg shadow-amber-500/20"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-600 mt-6 tracking-wider">
          Role is enforced by the server based on your account
        </p>
        <p className="text-center text-sm text-gray-400 mt-3">
          New cafe?{" "}
          <Link
            to="/register"
            data-testid="link-register"
            className="text-amber-300 hover:text-amber-200 font-bold uppercase tracking-wider"
          >
            Register here
          </Link>
        </p>
      </motion.div>

      <ForgotPasswordModal
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        initialEmail={email}
      />
    </div>
  );
}
